import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { RequirePermission } from "@/components/auth/RequirePermission";
import ErrorBoundary from "@/components/ErrorBoundary";
import ReloadWatcher from "@/components/ReloadWatcher";
import MaintenanceGate from "@/components/MaintenanceGate";

// Layouts
import MainLayout from "@/components/layouts/MainLayout";
import AuthLayout from "@/components/layouts/AuthLayout";

// Landing Page
import LandingPage from "@/pages/landing/LandingPage";
import GuiaDemoPage from "@/pages/landing/GuiaDemoPage";
import LegalCheckinTestPage from "@/pages/mockups/LegalCheckinTestPage";

// Auth Pages
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import VerifyEmailPage from "@/pages/auth/VerifyEmailPage";

// Legal Pages (públicas)
import TermsPage from "@/pages/legal/TermsPage";
import PrivacyPage from "@/pages/legal/PrivacyPage";

// Dashboard
import DashboardPage from "@/pages/dashboard/DashboardPage";

// Organization
import OrganizationPage from "@/pages/organization/OrganizationPage";
import ClubDetailPage from "@/pages/organization/ClubDetailPage";

// Members
import MembersPage from "@/pages/members/MembersPage";
import JoinRequestsPage from "@/pages/members/JoinRequestsPage";
import ProfileUpdateRequestsPage from "@/pages/members/ProfileUpdateRequestsPage";
import MemberDetailPage from "@/pages/members/MemberDetailPage";
import MemberFormPage from "@/pages/members/MemberFormPage";

// Progress
import ProgressPage from "@/pages/progress/ProgressPage";
import ClassApprovalPage from "@/pages/progress/ClassApprovalPage";

// Events
import EventsPage from "@/pages/events/EventsPage";
import EventDetailRouter from "@/pages/events/EventDetailRouter";
import EventClubDetailPage from "@/pages/events/EventClubDetailPage";
import PublicEventAgendaPage from "@/pages/events/PublicEventAgendaPage";

// Attendance
import AttendancePage from "@/pages/attendance/AttendancePage";

// Finance
import FinancePage from "@/pages/finance/FinancePage";

// Reports
import ReportsPage from "@/pages/reports/ReportsPage";

// Settings
import SettingsPage from "@/pages/settings/SettingsPage";

// Admin
import TeamPage from "@/pages/admin/TeamPage";
import RbacAdminPage from "@/pages/admin/RbacAdminPage";

// Profile
import ProfilePage from "@/pages/profile/ProfilePage";

// Notifications
import NotificationsPage from "@/pages/notifications/NotificationsPage";

// Design System
import DesignSystemPage from "@/pages/design-system/DesignSystemPage";

// 404
import NotFoundPage from "@/pages/NotFoundPage";
import AuthActionPage from "@/pages/auth/AuthActionPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import { LoadingScreen } from "@/components/ui";

// Onboarding
import SetupPage from "@/pages/onboarding/SetupPage";
import PendingApprovalPage from "@/pages/onboarding/PendingApprovalPage";
import ApprovedUnlinkedPage from "@/pages/onboarding/ApprovedUnlinkedPage";
import PendingClubApprovalPage from "@/pages/onboarding/PendingClubApprovalPage";

// Organization - Club Approval
import ClubApprovalRequestsPage from "@/pages/organization/ClubApprovalRequestsPage";
import SectionDirectorRequestsPage from "@/pages/organization/SectionDirectorRequestsPage";

// Home Route - redirects authenticated users to dashboard
function HomeRoute() {
  const {
    user,
    loading,
    needsSetup,
    approvedButUnlinked,
    pendingApproval,
    pendingClubApproval,
  } = useAuthStore();

  if (loading) {
    return <LoadingScreen message="Preparando tu experiencia…" />;
  }

  if (user) {
    if (approvedButUnlinked)
      return <Navigate to="/cuenta-sin-ficha" replace />;
    if (needsSetup) return <Navigate to="/setup" replace />;
    if (pendingApproval) return <Navigate to="/pending-approval" replace />;
    if (pendingClubApproval)
      return <Navigate to="/pending-club-approval" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return <LandingPage />;
}

// Authenticated Route - solo verifica que haya usuario autenticado (sin verificar roles)
function AuthenticatedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthStore();
  const location = useLocation();

  if (loading) {
    const isPendingOnboardingRoute =
      location.pathname === "/pending-approval" ||
      location.pathname === "/pending-club-approval";
    if (user && isPendingOnboardingRoute) {
      return <>{children}</>;
    }
    return <LoadingScreen message="Verificando tu sesión…" />;
  }

  if (!user) {
    const redirectTo = encodeURIComponent(location.pathname + location.search);
    // Si viene con invite, llevar a registro (cuenta nueva) no a login
    const hasInvite = location.search.includes("invite=");
    const authPath = hasInvite ? "/register" : "/login";
    return <Navigate to={`${authPath}?redirect=${redirectTo}`} replace />;
  }

  return <>{children}</>;
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const {
    user,
    loading,
    profile,
    needsSetup,
    approvedButUnlinked,
    pendingApproval,
    pendingClubApproval,
  } = useAuthStore();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen message="Cargando tu espacio…" />;
  }

  if (!user) {
    const redirectTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirectTo}`} replace />;
  }

  // F12-P1: aprobado pero la ficha es de otra cuenta → explicar, no re-pedir
  // datos (esta comprobación va ANTES de needsSetup a propósito).
  if (approvedButUnlinked && location.pathname !== "/profile") {
    return <Navigate to="/cuenta-sin-ficha" replace />;
  }

  // Redirigir a setup si necesita completar registro
  if (needsSetup) {
    return <Navigate to="/setup" replace />;
  }

  // Redirigir a pending si tiene solicitud pendiente
  // Excepto /profile que siempre es accesible
  if (pendingApproval && location.pathname !== "/profile") {
    return <Navigate to="/pending-approval" replace />;
  }

  // Redirigir a pending-club-approval si tiene solicitud de club pendiente
  if (pendingClubApproval && location.pathname !== "/profile") {
    return <Navigate to="/pending-club-approval" replace />;
  }

  // Si el usuario está pendiente de activación
  if (profile?.status === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-yellow-600 dark:text-yellow-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Cuenta pendiente de activación
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Tu cuenta está siendo revisada. Un administrador te asignará los
            permisos correspondientes pronto.
          </p>
          <button
            onClick={() => useAuthStore.getState().signOut()}
            className="btn-secondary"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// Captura el ?invite= en sessionStorage tan pronto como llega la URL,
// ANTES de cualquier redirect de autenticación. Así sobrevive login/register/verify.
// También extrae el invite code si viene embebido dentro de ?redirect=
function InviteCapture() {
  const [searchParams] = useSearchParams();
  useEffect(() => {
    // Caso 1: ?invite= directo en la URL (ej: /setup?invite=P66MFE)
    const invite = searchParams.get("invite");
    if (invite && invite.length >= 4) {
      const code = invite.toUpperCase();
      sessionStorage.setItem("pending_invite", code);
      localStorage.setItem("pending_invite", code);
      // Sello de tiempo: el código guardado caduca a los 14 días (ver
      // SetupPage). Sin esto, un enlace de invitación de hace meses seguía
      // preseleccionando aquel club y saltándose la elección de camino.
      localStorage.setItem("pending_invite_at", String(Date.now()));
      return;
    }
    // Caso 2: invite embebido dentro de ?redirect= (ej: /login?redirect=%2Fsetup%3Finvite%3DP66MFE)
    const redirect = searchParams.get("redirect");
    if (redirect) {
      const decoded = decodeURIComponent(redirect);
      const qIndex = decoded.indexOf("?");
      if (qIndex !== -1) {
        const inviteFromRedirect = new URLSearchParams(
          decoded.slice(qIndex + 1),
        ).get("invite");
        if (inviteFromRedirect && inviteFromRedirect.length >= 4) {
          const code = inviteFromRedirect.toUpperCase();
          sessionStorage.setItem("pending_invite", code);
          localStorage.setItem("pending_invite", code);
          localStorage.setItem("pending_invite_at", String(Date.now()));
        }
      }
    }
  }, [searchParams]);
  return null;
}

function App() {
  const initialize = useAuthStore((state) => state.initialize);

  useEffect(() => {
    const unsubscribe = initialize();
    return () => unsubscribe();
  }, [initialize]);

  return (
    <BrowserRouter>
      {/* InviteCapture FUERA de Routes para que siempre se ejecute */}
      <InviteCapture />
      <ReloadWatcher />
      <MaintenanceGate>
        <Routes>
          {/* Landing Page - Redirects to dashboard if authenticated */}
          <Route path="/" element={<HomeRoute />} />

          {/* Auth Action Handler (dispatcher para links de Firebase) */}
          <Route path="/auth/action" element={<AuthActionPage />} />

          {/* Legal Pages (públicas, sin layout de auth) */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* Guía paso a paso (pública) */}
          <Route path="/guia" element={<GuiaDemoPage />} />

          {/* Maqueta pública: demo visual del flujo de check-in legal */}
          <Route path="/legalchekintest" element={<LegalCheckinTestPage />} />

          {/* Agenda pública del evento (sin login) */}
          <Route path="/agenda/:eventId" element={<PublicEventAgendaPage />} />

          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
          </Route>

          {/* Onboarding Routes - Authenticated but no role yet */}
          <Route
            path="/setup"
            element={
              <AuthenticatedRoute>
                <SetupPage />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/pending-approval"
            element={
              <AuthenticatedRoute>
                <PendingApprovalPage />
              </AuthenticatedRoute>
            }
          />
          {/* F12-P1: aprobado pero la ficha pertenece a otra cuenta */}
          <Route
            path="/cuenta-sin-ficha"
            element={
              <AuthenticatedRoute>
                <ApprovedUnlinkedPage />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/pending-club-approval"
            element={
              <AuthenticatedRoute>
                <PendingClubApprovalPage />
              </AuthenticatedRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Organization - Protegido */}
            <Route
              path="/organization"
              element={
                <RequirePermission module="organization">
                  <OrganizationPage />
                </RequirePermission>
              }
            />
            <Route
              path="/organization/clubs/:clubId"
              element={
                <RequirePermission module="organization">
                  <ClubDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="/organization/club-requests"
              element={
                <RequirePermission
                  module="organization"
                  action="create"
                  redirectTo="/dashboard"
                >
                  <ClubApprovalRequestsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/organization/director-requests"
              element={
                <RequirePermission
                  module="organization"
                  action="create"
                  redirectTo="/dashboard"
                >
                  <SectionDirectorRequestsPage />
                </RequirePermission>
              }
            />

            {/* Members - Protegido */}
            <Route
              path="/members"
              element={
                <RequirePermission module="members">
                  <MembersPage />
                </RequirePermission>
              }
            />
            <Route
              path="/members/new"
              element={
                <RequirePermission module="members" action="create">
                  <MemberFormPage />
                </RequirePermission>
              }
            />
            <Route
              path="/members/:id"
              element={
                <RequirePermission module="members">
                  <MemberDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="/members/:id/edit"
              element={
                <RequirePermission module="members" action="edit">
                  <MemberFormPage />
                </RequirePermission>
              }
            />
            <Route
              path="/members/requests"
              element={
                <RequirePermission module="members">
                  <JoinRequestsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/members/profile-requests"
              element={
                <RequirePermission module="members">
                  <ProfileUpdateRequestsPage />
                </RequirePermission>
              }
            />

            {/* Progress - Protegido */}
            <Route
              path="/progress"
              element={
                <RequirePermission module="progress">
                  <ProgressPage />
                </RequirePermission>
              }
            />
            <Route
              path="/progress/approvals"
              element={
                <RequirePermission module="progress">
                  <ClassApprovalPage />
                </RequirePermission>
              }
            />

            {/* Events - Protegido */}
            <Route
              path="/events"
              element={
                <RequirePermission module="events">
                  <EventsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/events/:id"
              element={
                <RequirePermission module="events">
                  <EventDetailRouter />
                </RequirePermission>
              }
            />
            <Route
              path="/events/:eventId/clubs/:clubId"
              element={
                <RequirePermission module="events">
                  <EventClubDetailPage />
                </RequirePermission>
              }
            />

            {/* Attendance - Protegido */}
            <Route
              path="/attendance"
              element={
                <RequirePermission module="attendance">
                  <AttendancePage />
                </RequirePermission>
              }
            />

            {/* Finance - Protegido */}
            <Route
              path="/finance"
              element={
                <RequirePermission module="finance">
                  <FinancePage />
                </RequirePermission>
              }
            />

            {/* Reports - Protegido */}
            <Route
              path="/reports"
              element={
                <RequirePermission module="reports">
                  <ReportsPage />
                </RequirePermission>
              }
            />

            {/* Admin/Equipo - Protegido (solo admins) */}
            <Route
              path="/team"
              element={
                <RequirePermission module="users" redirectTo="/dashboard">
                  <TeamPage />
                </RequirePermission>
              }
            />
            {/* Admin/RBAC - Solo admins de asociación/unión */}
            <Route
              path="/admin/rbac"
              element={
                <RequirePermission module="users" redirectTo="/dashboard">
                  <RbacAdminPage />
                </RequirePermission>
              }
            />
            {/* Redirects from old routes */}
            <Route path="/users" element={<Navigate to="/team" replace />} />
            <Route path="/roles" element={<Navigate to="/team" replace />} />

            {/* Settings - Protegido */}
            <Route
              path="/settings"
              element={
                <RequirePermission module="settings">
                  <SettingsPage />
                </RequirePermission>
              }
            />

            {/* Profile - Accesible para todos */}
            <Route path="/profile" element={<ProfilePage />} />

            {/* Notifications - Accesible para todos los autenticados */}
            <Route path="/notifications" element={<NotificationsPage />} />

            {/* Design System - Solo desarrollo */}
            {import.meta.env.DEV && (
              <Route path="/design-system" element={<DesignSystemPage />} />
            )}
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </MaintenanceGate>
    </BrowserRouter>
  );
}

function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithErrorBoundary;
